import uPlot from 'uplot';

import {type AnnotationItem} from '../annotations';

const ISSUE_LINE_COLOR = '#FFA8B0';
const ISSUE_LINE_COLOR_HOVERED = '#FF3D5A';
const LINE_WIDTH_CSS_PX = 2;

export type IssueLinesConfig = {
  issueLines: AnnotationItem[];
  /**
   * Read on every draw, so a hover change only needs a redraw. Passing the
   * hovered issue as a value would change the chart options and rebuild it.
   */
  getHoveredIssue: () => string | null;
  isZoomedXMin: boolean;
  isZoomedXMax: boolean;
};

export function createDrawIssueLinesHook(
  config: IssueLinesConfig
): (u: uPlot) => void {
  const {issueLines, getHoveredIssue, isZoomedXMin, isZoomedXMax} = config;

  return (u: uPlot) => {
    if (issueLines.length === 0) {
      return;
    }

    const hoveredIssue = getHoveredIssue();

    const ctx = u.ctx;
    const xScale = u.scales.x;
    const yScale = u.scales.y;

    if (!xScale || !yScale) {
      return;
    }

    const xMin = xScale.min ?? 0;
    const xMax = xScale.max ?? 1;
    const yMin = yScale.min ?? 0;
    const yMax = yScale.max ?? 1;

    // Get pixel positions for the plot area
    const plotLeft = u.valToPos(xMin, 'x', true);
    const plotRight = u.valToPos(xMax, 'x', true);
    const plotTop = u.valToPos(yMax, 'y', true);
    const plotBottom = u.valToPos(yMin, 'y', true);

    // Fixed line width in canvas pixels
    const pxRatio = uPlot.pxRatio;
    const lineWidth = LINE_WIDTH_CSS_PX * pxRatio;

    ctx.save();

    for (const issueLine of issueLines) {
      const {status, xValue, id} = issueLine;

      let xPx: number;
      let color: string;

      if (status === 'before') {
        // Only show 'before' lines when not zoomed in on x-axis min
        if (isZoomedXMin) {
          continue;
        }
        // Render at left edge
        xPx = plotLeft;
        color = ISSUE_LINE_COLOR;
      } else if (status === 'after') {
        // Only show 'after' lines when not zoomed out on x-axis max
        if (isZoomedXMax) {
          continue;
        }
        // Render at right edge
        xPx = plotRight - lineWidth;
        color = ISSUE_LINE_COLOR;
      } else {
        // 'within', 'at_start', 'at_end' - render at actual position, centered on the point
        xPx = u.valToPos(xValue, 'x', true) - lineWidth / 2;

        // Skip if outside visible range
        if (xPx + lineWidth < plotLeft || xPx > plotRight) {
          continue;
        }

        // Only these statuses support hover highlighting
        color =
          hoveredIssue === id ? ISSUE_LINE_COLOR_HOVERED : ISSUE_LINE_COLOR;
      }

      ctx.fillStyle = color;
      ctx.fillRect(xPx, plotTop, lineWidth, plotBottom - plotTop);
    }

    ctx.restore();
  };
}
